import { FormEvent, useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { Badge, Button, Card, PageHeader } from "@/components/primitives";
import { ConsultantCandidate, useCosmosClient } from "@/lib/cosmos-api";

export function CoursesPage() {
  const cosmos = useCosmosClient();
  const [query, setQuery] = useState("");
  const [courses, setCourses] = useState<ConsultantCandidate[]>([]);
  const [source, setSource] = useState<"postgres" | "seed">("seed");
  const [error, setError] = useState<string | null>(null);

  const load = async (event?: FormEvent) => {
    event?.preventDefault();
    setError(null);
    try {
      const response = await cosmos.listCourses({ query, limit: 24 });
      setCourses(response.courses);
      setSource(response.source);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load courses");
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="page">
      <PageHeader
        eyebrow="Courses"
        title="AIHR course catalog"
        subtitle="Search courses and ask Cosmos course-scoped questions."
        actions={<Badge tone={source === "postgres" ? "success" : "warning"}>{source}</Badge>}
      />
      <form className="search-row" onSubmit={(event) => void load(event)}>
        <Search size={16} />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search by course title, domain, or difficulty"
        />
        <Button>Search</Button>
      </form>
      {error && <Card className="error-card">{error}</Card>}
      <div className="card-grid">
        {courses.map((course) => (
          <Link key={course.id} to="/courses/$courseId" params={{ courseId: course.id }}>
            <Card className="course-card">
              <Badge tone="ai">{course.difficulty ?? "course"}</Badge>
              <h2>{course.title}</h2>
              <p>{course.description}</p>
              <div className="inline-list">
                {course.domainTags.slice(0, 3).map((tag) => (
                  <Badge key={tag}>{tag}</Badge>
                ))}
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
